import React, { useEffect, useRef } from "react";
import { Paperclip } from "lucide-react";

const formatBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
};

interface ChatComposerProps {
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  onSend: () => void;
  onAttach?: (files: File[]) => void;
  attachments?: File[];
  onRemoveAttachment?: (i: number) => void;
  placeholder?: string;
}

const ChatComposer: React.FC<ChatComposerProps> = ({
  value,
  disabled,
  onChange,
  onSend,
  onAttach,
  attachments = [],
  onRemoveAttachment,
  placeholder,
}) => {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    // Hauteur maximale plus petite sur mobile
    const maxHeight = window.innerWidth < 640 ? 120 : 240;
    el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
  }, [value]);

  const handleAttachClick = () => fileInputRef.current?.click();

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length && onAttach) onAttach(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isExpanded = value.trim().length >= 80 || attachments.length > 0;

  return (
    <div>
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2 px-2 pb-2">
          {attachments.map((f, i) => (
            <div
              key={`${f.name}-${i}`}
              className="flex items-center gap-2 px-2 py-1 rounded-lg border bg-gray-50"
            >
              <Paperclip size={14} className="text-gray-500" />
              <span
                className="text-sm text-gray-700 truncate max-w-[180px]"
                title={f.name}
              >
                {f.name}
              </span>
              <span className="text-xs text-gray-400">({formatBytes(f.size)})</span>
              {onRemoveAttachment && (
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600"
                  onClick={() => onRemoveAttachment(i)}
                  aria-label="Retirer le fichier"
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

          {/* Compact search bar ou Expanded message composer */}
          <div className={`shadow-[0_4px_20px_rgba(0,0,0,0.15)] transition-all duration-300 ${
            isExpanded 
              ? 'rounded-2xl sm:rounded-3xl p-3 sm:p-4' 
              : 'rounded-full px-3 sm:px-4 py-2 sm:py-3'
          }`}>
        <div className={`flex items-center gap-2 sm:gap-3 ${isExpanded ? 'items-start' : 'items-center'}`}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={handleFileChange}
          />

          {/* Text Input */}
          <textarea
            ref={ref}
            className={`flex-1 bg-transparent outline-none border-0 focus:border-0 focus:ring-0 placeholder:text-neutral-400 resize-none ${
              disabled 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-black'
            } ${
              isExpanded 
                ? 'text-lg sm:text-[22px] leading-6 sm:leading-8 min-h-[30px] max-h-32' 
                : 'text-sm sm:text-[18px] min-h-[20px] max-h-20'
            }`}
            placeholder={placeholder || "Ask anything"}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            disabled={disabled}
          />

          {/* Right side buttons */}
          {isExpanded ? (
            <div className="flex flex-col items-center gap-2 pt-1">
              {/* Plus Button */}
              <button
                onClick={handleAttachClick}
                className="rounded-full h-9 w-9 text-neutral-200 hover:bg-neutral-700 flex items-center justify-center transition-colors"
                title="Plus d'options"
                type="button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>

              {/* Microphone Button */}
              <button
                className="rounded-full h-9 w-9 text-neutral-200 hover:bg-neutral-700 flex items-center justify-center transition-colors"
                title="Enregistrement vocal"
                type="button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>

              {/* Send Button - Arrow up icon */}
              <button
                onClick={onSend}
                disabled={disabled || (!value.trim() && attachments.length === 0)}
                className="rounded-full h-12 w-12 bg-neutral-200 text-neutral-900 hover:bg-neutral-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                title="Envoyer"
                type="button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                </svg>
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              {/* Plus Button - Compact */}
              <button
                onClick={handleAttachClick}
                className="rounded-full h-7 w-7 sm:h-8 sm:w-8 text-neutral-200 hover:bg-neutral-700 flex items-center justify-center transition-colors"
                title="Plus d'options"
                type="button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>

              {/* Microphone Button - Compact */}
              <button
                className="rounded-full h-7 w-7 sm:h-8 sm:w-8 text-neutral-200 hover:bg-neutral-700 flex items-center justify-center transition-colors"
                title="Enregistrement vocal"
                type="button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                </svg>
              </button>

              {/* Send Button - Compact */}
              <button
                onClick={onSend}
                disabled={disabled || (!value.trim() && attachments.length === 0)}
                className="rounded-full h-7 w-7 sm:h-8 sm:w-8 bg-neutral-700/80 text-neutral-200 hover:bg-neutral-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                title="Envoyer"
                type="button"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19V5m0 0l-7 7m7-7l7 7" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="px-3 pt-2 text-xs text-neutral-400">
          Entrée pour envoyer • Maj+Entrée pour un retour à la ligne
        </div>
      )}
    </div>
  );
};

export default ChatComposer;
